import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';

// Дії переглядача залежать від платформи: веб — завантаження / Web Share, mobile — нативні плагіни.
// Повернутий рядок переглядач показує підказкою (напр. «Збережено в Фото»)
export type ImageViewerAction = (url: string) => Promise<string | void>;

export interface ImageViewerActions {
  save?: ImageViewerAction;
  share?: ImageViewerAction;
}

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_MS = 300;

/**
 * Повноекранний перегляд зображення з чату: зум щипком двома пальцями, колесом миші й подвійним дотиком/кліком,
 * перетягування збільшеного зображення, «Зберегти» і «Переслати». Без залежностей від Ionic/Material —
 * однаково працює у веб-CRM і в мобільному застосунку (відкривається через ImageViewerService).
 */
@Component({
  selector: 'shared-image-viewer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="iv" (click)="onBackdrop($event)">
      <div class="iv__bar" (click)="$event.stopPropagation()">
        <button class="iv__btn" type="button" aria-label="Закрити" (click)="close()">
          <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
        <span class="iv__spacer"></span>
        @if (actions.save) {
          <button class="iv__btn" type="button" aria-label="Зберегти" [disabled]="busy" (click)="run(actions.save)">
            <svg viewBox="0 0 24 24"><path d="M12 4v11m0 0l-4.5-4.5M12 15l4.5-4.5M5 19h14"/></svg>
            <span class="iv__label">Зберегти</span>
          </button>
        }
        @if (actions.share) {
          <button class="iv__btn" type="button" aria-label="Переслати" [disabled]="busy" (click)="run(actions.share)">
            <svg viewBox="0 0 24 24"><path d="M14 5l7 7-7 7M21 12H10a7 7 0 0 0-7 7"/></svg>
            <span class="iv__label">Переслати</span>
          </button>
        }
      </div>

      <div class="iv__stage" #stage
           (pointerdown)="onPointerDown($event)" (pointermove)="onPointerMove($event)"
           (pointerup)="onPointerUp($event)" (pointercancel)="onPointerUp($event)"
           (wheel)="onWheel($event)" (dblclick)="onDoubleClick($event)">
        <img class="iv__img" [src]="url" alt="" draggable="false" (load)="loaded = true"
             [style.transform]="'translate(' + x + 'px,' + y + 'px) scale(' + scale + ')'"
             [class.iv__img--animate]="animate" (click)="$event.stopPropagation()">
        @if (!loaded) {
          <div class="iv__spinner"></div>
        }
      </div>

      @if (message) {
        <div class="iv__toast">{{ message }}</div>
      }
    </div>
  `,
  styles: [`
    :host { position: fixed; inset: 0; z-index: 50000; }
    .iv { position: absolute; inset: 0; display: flex; flex-direction: column; background: rgba(0, 0, 0, 0.94);
      color: #fff; animation: iv-in 0.18s ease-out; user-select: none; -webkit-user-select: none; }
    .iv__bar { display: flex; align-items: center; gap: 4px; position: relative; z-index: 2;
      padding: calc(env(safe-area-inset-top) + 8px) 8px 8px; }
    .iv__spacer { flex: 1; }
    .iv__btn { display: inline-flex; align-items: center; gap: 6px; height: 40px; min-width: 40px; padding: 0 10px;
      border: none; border-radius: 20px; background: rgba(255, 255, 255, 0.12); color: #fff; font: 500 14px/1 inherit;
      cursor: pointer; }
    .iv__btn:hover { background: rgba(255, 255, 255, 0.2); }
    .iv__btn:disabled { opacity: 0.5; cursor: default; }
    .iv__btn svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round;
      stroke-linejoin: round; }
    .iv__stage { flex: 1; position: relative; overflow: hidden; display: flex; align-items: center; justify-content: center;
      touch-action: none; padding-bottom: env(safe-area-inset-bottom); }
    .iv__img { max-width: 100%; max-height: 100%; object-fit: contain; transform-origin: center center; will-change: transform;
      cursor: zoom-in; }
    .iv__img--animate { transition: transform 0.2s ease; }
    .iv__spinner { position: absolute; width: 32px; height: 32px; border-radius: 50%; border: 3px solid rgba(255, 255, 255, 0.25);
      border-top-color: #fff; animation: iv-spin 0.8s linear infinite; }
    .iv__toast { position: absolute; left: 50%; bottom: calc(env(safe-area-inset-bottom) + 24px); transform: translateX(-50%);
      max-width: calc(100% - 32px); padding: 10px 16px; border-radius: 12px; background: rgba(255, 255, 255, 0.16);
      font-size: 14px; text-align: center; }
    @media (max-width: 420px) { .iv__label { display: none; } }
    @keyframes iv-in { from { opacity: 0; } to { opacity: 1; } }
    @keyframes iv-spin { to { transform: rotate(360deg); } }
  `]
})
export class ImageViewerComponent {
  @Input({ required: true }) url!: string;
  @Input() actions: ImageViewerActions = {};
  @Output() closed = new EventEmitter<void>();
  @ViewChild('stage', { static: true }) stage!: ElementRef<HTMLElement>;

  scale = 1;
  x = 0;
  y = 0;
  animate = false;
  loaded = false;
  busy = false;
  message = '';

  private pointers = new Map<number, { x: number; y: number }>();
  private pinch: { distance: number; scale: number } | null = null;
  private pan: { x: number; y: number; startX: number; startY: number } | null = null;
  private lastTap = 0;
  private moved = false;
  private lastPointerType = '';

  constructor(private cdr: ChangeDetectorRef) {}

  @HostListener('document:keydown.escape')
  close(): void {
    this.closed.emit();
  }

  // дотик по фону (не збільшеного зображення) — закрити, як у месенджерах
  onBackdrop(event: MouseEvent): void {
    if (this.scale === 1 && !this.moved && event.target === this.stage.nativeElement) this.close();
  }

  async run(action?: ImageViewerAction): Promise<void> {
    if (!action || this.busy) return;
    this.busy = true;
    this.cdr.markForCheck();
    try {
      const message = await action(this.url);
      if (message) this.toast(message);
    } catch (err: any) {
      // скасування системного вікна «Поділитися» — не помилка
      if (!/cancel|abort/i.test(String(err?.message || err))) this.toast('Не вдалося: ' + (err?.message || 'спробуйте ще раз'));
    } finally {
      this.busy = false;
      this.cdr.markForCheck();
    }
  }

  toast(message: string): void {
    this.message = message;
    this.cdr.markForCheck();
    setTimeout(() => {
      if (this.message === message) {
        this.message = '';
        this.cdr.markForCheck();
      }
    }, 2500);
  }

  // миша — подвійний клік; на сенсорному екрані подвійний дотик обробляє onPointerUp (dblclick там дублювався б)
  onDoubleClick(event: MouseEvent): void {
    if (this.lastPointerType !== 'touch') this.toggleZoom(event);
  }

  toggleZoom(event: { clientX: number; clientY: number }): void {
    this.animate = true;
    if (this.scale > 1) {
      this.reset();
    } else {
      // збільшуємо навколо точки дотику
      const rect = this.stage.nativeElement.getBoundingClientRect();
      const cx = event.clientX - rect.left - rect.width / 2;
      const cy = event.clientY - rect.top - rect.height / 2;
      this.scale = DOUBLE_TAP_SCALE;
      this.x = -cx * (DOUBLE_TAP_SCALE - 1);
      this.y = -cy * (DOUBLE_TAP_SCALE - 1);
      this.clampPan();
    }
    this.cdr.markForCheck();
  }

  onWheel(event: WheelEvent): void {
    event.preventDefault();
    this.animate = false;
    const next = this.clampScale(this.scale * (event.deltaY < 0 ? 1.15 : 1 / 1.15));
    this.zoomAround(event.clientX, event.clientY, next);
  }

  onPointerDown(event: PointerEvent): void {
    (event.target as Element).setPointerCapture?.(event.pointerId);
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.lastPointerType = event.pointerType;
    this.animate = false;
    this.moved = false;
    if (this.pointers.size === 2) {
      this.pinch = { distance: this.distance(), scale: this.scale };
      this.pan = null;
    } else if (this.pointers.size === 1) {
      this.pan = { x: this.x, y: this.y, startX: event.clientX, startY: event.clientY };
    }
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.pointers.has(event.pointerId)) return;
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pinch && this.pointers.size >= 2) {
      const [a, b] = [...this.pointers.values()];
      const next = this.clampScale(this.pinch.scale * (this.distance() / this.pinch.distance));
      this.zoomAround((a.x + b.x) / 2, (a.y + b.y) / 2, next);
      this.moved = true;
    } else if (this.pan && this.scale > 1) {
      const dx = event.clientX - this.pan.startX;
      const dy = event.clientY - this.pan.startY;
      if (Math.abs(dx) + Math.abs(dy) > 4) this.moved = true;
      this.x = this.pan.x + dx;
      this.y = this.pan.y + dy;
      this.clampPan();
      this.cdr.markForCheck();
    } else if (this.pan && Math.abs(event.clientX - this.pan.startX) + Math.abs(event.clientY - this.pan.startY) > 4) {
      this.moved = true;
    }
  }

  onPointerUp(event: PointerEvent): void {
    this.pointers.delete(event.pointerId);
    if (this.pointers.size < 2) this.pinch = null;
    if (this.pointers.size === 0) {
      this.pan = null;
      if (this.scale <= 1.02) this.reset();
      // подвійний дотик на сенсорному екрані (dblclick там ненадійний)
      if (event.pointerType === 'touch' && !this.moved) {
        const now = Date.now();
        if (now - this.lastTap < DOUBLE_TAP_MS) {
          this.lastTap = 0;
          this.toggleZoom(event);
        } else {
          this.lastTap = now;
        }
      }
    } else if (this.pointers.size === 1) {
      const [rest] = [...this.pointers.values()];
      this.pan = { x: this.x, y: this.y, startX: rest.x, startY: rest.y };
    }
  }

  private zoomAround(clientX: number, clientY: number, next: number): void {
    const rect = this.stage.nativeElement.getBoundingClientRect();
    const cx = clientX - rect.left - rect.width / 2;
    const cy = clientY - rect.top - rect.height / 2;
    const ratio = next / this.scale;
    this.x = cx - (cx - this.x) * ratio;
    this.y = cy - (cy - this.y) * ratio;
    this.scale = next;
    if (this.scale <= 1) this.reset();
    else this.clampPan();
    this.cdr.markForCheck();
  }

  // не даємо відтягнути збільшене зображення далеко за край екрана
  private clampPan(): void {
    const rect = this.stage.nativeElement.getBoundingClientRect();
    const maxX = (rect.width * (this.scale - 1)) / 2;
    const maxY = (rect.height * (this.scale - 1)) / 2;
    this.x = Math.max(-maxX, Math.min(maxX, this.x));
    this.y = Math.max(-maxY, Math.min(maxY, this.y));
  }

  private clampScale(scale: number): number {
    return Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));
  }

  private distance(): number {
    const [a, b] = [...this.pointers.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  private reset(): void {
    this.scale = 1;
    this.x = 0;
    this.y = 0;
  }
}
